/**
 * useComments Hook
 * Fetch and create comments for tickets
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import { queryKeys } from '@/lib/query-client';
import { CommentCreate, CommentResponse } from '@/types';
import { toast } from 'sonner';

export function useComments(ticketId: string) {
  return useQuery({
    queryKey: queryKeys.comments.list(ticketId),
    queryFn: async () => {
      const { data } = await apiClient.get<CommentResponse[]>(
        `/tickets/${ticketId}/comments`
      );
      return data;
    },
    enabled: !!ticketId,
  });
}

export function useCreateComment(ticketId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (comment: CommentCreate) => {
      const { data } = await apiClient.post<CommentResponse>(
        `/tickets/${ticketId}/comments`,
        comment
      );
      return data;
    },
    onSuccess: () => {
      // Invalidate comments list
      queryClient.invalidateQueries({ queryKey: queryKeys.comments.list(ticketId) });

      // Increment comment count in ticket cache
      queryClient.setQueryData(
        queryKeys.tickets.detail(ticketId),
        (oldData: any) => {
          if (!oldData) return oldData;
          return {
            ...oldData,
            comment_count: oldData.comment_count + 1,
          };
        }
      );

      toast.success('Comment added');
    },
    onError: (error: any) => {
      const message = error.response?.data?.detail || 'Failed to add comment';
      toast.error(message);
    },
  });
}
